// Loot rolls (Phaser-free): rarity tables, orb reward pools, pity rules,
// choices, shop offers and major upgrades. Everything draws from the run's
// loot stream so a seed reproduces the same rewards regardless of combat.
//
// Pity (run.pity):
//   sinceAdvanced >= 4  → the next reward is at least advanced
//   sinceWeapon   >= 2  → an arsenal orb forces a new weapon while the player
//                         has fewer than 2 weapons (or only the starter)
//   sinceUseful   >= 3  → prefer rewards that fill an empty slot, complete a
//                         synergy that is one tag short, or have a new item id

import type { InventoryApi } from '../../core/contracts';
import { Rng } from '../../core/rng';
import type {
  ItemInstance,
  ItemKind,
  LootSource,
  MajorUpgradeDef,
  MechDef,
  OrbType,
  PityState,
  Rarity,
  RunStats,
  ShopOffer,
} from '../../core/types';
import { ORB_TYPES } from '../../core/types';
import {
  EQUIPMENT,
  EQUIP_DEF_IDS,
  MAJOR_UPGRADES,
  MODS,
  MOD_DEF_IDS,
  MUTATIONS,
  MUTATION_DEF_IDS,
  UPGRADE_DEF_IDS,
  WEAPONS,
  WEAPON_DEF_IDS,
} from '../../data/items';
import { itemKindOf, makeItem, rarityRank } from './items';
import { MAX_MUTATIONS } from './loadout';

// ───────────────────────────── Context ─────────────────────────────

/** A set of def ids already presented this run (RunState.flags adapter in-game). */
export interface SeenSet {
  has(id: string): boolean;
  add(id: string): void;
}

export interface LootRollContext {
  /** run.rng.loot */
  rng: Rng;
  /** run.pity (mutated by tracked rolls). */
  pity: PityState;
  inventory: InventoryApi;
  mech: MechDef;
  /** save.isUnlocked */
  isUnlocked(id: string | undefined): boolean;
  /** run.stats (itemsFound is counted by roll()). */
  stats?: Pick<RunStats, 'itemsFound'>;
  /** Ids presented before this run (for the "new item id" pity test). */
  seen?: SeenSet;
  /** Ids lying on the floor right now (soft-avoided so reveals do not repeat). */
  avoid?: readonly string[];
}

// ───────────────────────────── Rarity ─────────────────────────────

/** [standard, advanced, prototype] weights per source. */
export const RARITY_WEIGHTS: Readonly<Record<LootSource, readonly [number, number, number]>> = {
  room: [72, 24, 4],
  loot_room: [60, 32, 8],
  elite: [40, 45, 15],
  boss: [15, 50, 35],
  shop: [55, 35, 10],
  crate: [85, 14, 1],
  challenge: [50, 40, 10],
  debug: [34, 33, 33],
};

/** Unstable orbs move this many points from standard (then advanced) to prototype. */
export const UNSTABLE_SHIFT = 10;

export function rarityWeights(source: LootSource, unstable = false): [number, number, number] {
  let [s, a, p] = RARITY_WEIGHTS[source] ?? RARITY_WEIGHTS.room;
  if (unstable) {
    const fromS = Math.min(s, UNSTABLE_SHIFT);
    const fromA = Math.min(a, UNSTABLE_SHIFT - fromS);
    s -= fromS;
    a -= fromA;
    p += fromS + fromA;
  }
  return [s, a, p];
}

export function rollRarity(
  source: LootSource,
  rng: Rng,
  opts: { unstable?: boolean; guaranteeAdvanced?: boolean } = {},
): Rarity {
  const [s0, a, p] = rarityWeights(source, opts.unstable);
  const s = opts.guaranteeAdvanced ? 0 : s0;
  const total = s + a + p;
  if (total <= 0) return 'advanced';
  const r = rng.next() * total;
  if (r < s) return 'standard';
  if (r < s + a) return 'advanced';
  return 'prototype';
}

// ───────────────────────────── Candidate pools ─────────────────────────────

interface Candidate {
  kind: ItemKind;
  id: string;
  weight: number;
  /** Forced rarity (unstable-orb equipment is always prototype). */
  rarity?: Rarity;
}

/** Category shares inside each orb (weights inside a category follow the defs). */
const ORB_SHARES: Readonly<Record<OrbType, readonly { kind: ItemKind; share: number; rarity?: Rarity }[]>> = {
  arsenal: [
    { kind: 'weapon', share: 0.65 },
    { kind: 'mod', share: 0.35 },
  ],
  armor: [{ kind: 'equipment', share: 1 }],
  unstable: [
    { kind: 'mutation', share: 0.7 },
    { kind: 'equipment', share: 0.3, rarity: 'prototype' },
  ],
};

/** Armor orbs weight equipment for empty slots this much higher. */
export const EMPTY_SLOT_BIAS = 2.5;
/** Weight multiplier for ids already lying on the floor. */
const AVOID_BIAS = 0.15;

/** Def ids equipped right now (weapons, equipment, mutations). Mods may repeat. */
export function equippedDefIds(inv: InventoryApi): Set<string> {
  const ids = new Set<string>();
  for (const w of inv.weapons) if (w) ids.add(w.defId);
  for (const e of Object.values(inv.equipment)) if (e) ids.add(e.defId);
  for (const m of inv.mutations) ids.add(m.defId);
  return ids;
}

/** Usable by this mech and unlocked in the meta save. */
export function isAvailable(c: LootRollContext, kind: ItemKind, id: string): boolean {
  switch (kind) {
    case 'weapon':
      return !!WEAPONS[id as keyof typeof WEAPONS] && c.isUnlocked(WEAPONS[id as keyof typeof WEAPONS].unlock);
    case 'equipment':
      return !!EQUIPMENT[id as keyof typeof EQUIPMENT] && c.isUnlocked(EQUIPMENT[id as keyof typeof EQUIPMENT].unlock);
    case 'mod':
      return !!MODS[id as keyof typeof MODS];
    case 'mutation': {
      const def = MUTATIONS[id as keyof typeof MUTATIONS];
      return !!def && c.isUnlocked(def.unlock) && (!def.mech || def.mech === c.mech.id);
    }
  }
}

function idsOf(kind: ItemKind): readonly string[] {
  switch (kind) {
    case 'weapon':
      return WEAPON_DEF_IDS;
    case 'equipment':
      return EQUIP_DEF_IDS;
    case 'mod':
      return MOD_DEF_IDS;
    case 'mutation':
      return MUTATION_DEF_IDS;
  }
}

function baseWeight(kind: ItemKind, id: string): number {
  switch (kind) {
    case 'weapon':
      return WEAPONS[id as keyof typeof WEAPONS].weight;
    case 'equipment':
      return EQUIPMENT[id as keyof typeof EQUIPMENT].weight;
    case 'mod':
      return MODS[id as keyof typeof MODS].weight;
    case 'mutation':
      return MUTATIONS[id as keyof typeof MUTATIONS].weight;
  }
}

function buildCandidates(c: LootRollContext, type: OrbType, exclude: ReadonlySet<string>): Candidate[] {
  const owned = equippedDefIds(c.inventory);
  const out: Candidate[] = [];
  for (const cat of ORB_SHARES[type]) {
    const list: Candidate[] = [];
    for (const id of idsOf(cat.kind)) {
      if (exclude.has(id)) continue;
      // Never offer an exact duplicate of an equipped item (mods excepted).
      if (cat.kind !== 'mod' && owned.has(id)) continue;
      if (!isAvailable(c, cat.kind, id)) continue;
      let w = baseWeight(cat.kind, id);
      if (cat.kind === 'equipment' && type === 'armor') {
        const slot = EQUIPMENT[id as keyof typeof EQUIPMENT].slot;
        if (!c.inventory.equipment[slot]) w *= EMPTY_SLOT_BIAS;
      }
      if (w > 0) list.push({ kind: cat.kind, id, weight: w, rarity: cat.rarity });
    }
    const total = list.reduce((sum, x) => sum + x.weight, 0);
    if (total <= 0) continue;
    for (const x of list) out.push({ ...x, weight: (x.weight / total) * cat.share });
  }
  return out;
}

/** True while the arsenal weapon pity may force a weapon. */
export function needsWeapon(inv: InventoryApi, mech: MechDef): boolean {
  const held = inv.weapons.filter((w): w is NonNullable<typeof w> => !!w);
  return held.length < 2 || held.every((w) => w.defId === mech.startingWeapon);
}

// A private stream for throwaway template instances so usefulness checks never
// consume the run's loot randomness.
const templateRng = new Rng(0x7e3a11);

/**
 * "Useful" = fills an empty slot, completes a synergy, or is an item id the
 * player has never been shown this run.
 */
export function isUseful(c: LootRollContext, item: ItemInstance): boolean {
  const inv = c.inventory;
  const owned = equippedDefIds(inv);
  if (!owned.has(item.defId) && !(c.seen?.has(item.defId) ?? false)) return true;
  const fills = item.kind === 'mutation' ? inv.mutations.length < MAX_MUTATIONS : inv.canAutoEquip(item);
  if (fills) return true;
  return inv.compare(item, c.mech.kitTags).synergiesGained.length > 0;
}

function candidateUseful(c: LootRollContext, cand: Candidate): boolean {
  const tpl = makeItem(cand.id, 'standard', templateRng);
  return !!tpl && isUseful(c, tpl);
}

interface RollOptions {
  exclude?: ReadonlySet<string>;
  /** Apply pity rules (forced weapon / guaranteed advanced / useful preference). */
  applyPity?: boolean;
}

/** One reward from an orb type's pool. Does not touch pity counters. */
function rollOne(c: LootRollContext, type: OrbType, source: LootSource, opts: RollOptions): ItemInstance {
  const exclude = opts.exclude ?? new Set<string>();
  let cands = buildCandidates(c, type, exclude);

  if (opts.applyPity) {
    if (type === 'arsenal' && c.pity.sinceWeapon >= 2 && needsWeapon(c.inventory, c.mech)) {
      const weapons = cands.filter((x) => x.kind === 'weapon');
      if (weapons.length) cands = weapons;
    }
    if (c.pity.sinceUseful >= 3) {
      const useful = cands.filter((x) => candidateUseful(c, x));
      if (useful.length) cands = useful;
    }
  }
  if (c.avoid?.length) {
    cands = cands.map((x) => (c.avoid!.includes(x.id) ? { ...x, weight: x.weight * AVOID_BIAS } : x));
  }

  let pick = c.rng.weighted(cands, (x) => x.weight);
  if (!pick) {
    // Pool exhausted (everything equipped / excluded): any other orb's pool, mods last.
    for (const t of ORB_TYPES) {
      if (t === type) continue;
      pick = c.rng.weighted(buildCandidates(c, t, exclude), (x) => x.weight);
      if (pick) break;
    }
  }
  if (!pick) {
    const mods = MOD_DEF_IDS.filter((id) => !exclude.has(id));
    pick = { kind: 'mod', id: mods.length ? c.rng.pick(mods) : c.rng.pick(MOD_DEF_IDS), weight: 1 };
  }

  const rarity =
    pick.rarity ??
    rollRarity(source, c.rng, {
      unstable: type === 'unstable',
      guaranteeAdvanced: !!opts.applyPity && c.pity.sinceAdvanced >= 4,
    });
  const bonusModSlots = c.inventory.upgrades.includes('mod_slot') ? 1 : 0;
  // makeItem only returns null for unknown ids; every candidate id comes from the tables.
  return makeItem(pick.id, rarity, c.rng, { bonusModSlots })!;
}

/** Update pity counters for one reward event (a single orb or one offer of choices). */
function notePity(c: LootRollContext, type: OrbType | 'mixed', items: readonly ItemInstance[], useful: boolean): void {
  const p = c.pity;
  const best = items.reduce((m, i) => Math.max(m, rarityRank(i.rarity)), 0);
  p.sinceAdvanced = best >= 1 ? 0 : p.sinceAdvanced + 1;
  const gotWeapon = items.some((i) => i.kind === 'weapon');
  if (gotWeapon) p.sinceWeapon = 0;
  else if (type === 'arsenal' || type === 'mixed') p.sinceWeapon += 1;
  p.sinceUseful = useful ? 0 : p.sinceUseful + 1;
  for (const i of items) c.seen?.add(i.defId);
}

// ───────────────────────────── Public rolls ─────────────────────────────

/** Single orb reward (pity applied and updated, itemsFound counted). */
export function rollReward(c: LootRollContext, type: OrbType, source: LootSource): ItemInstance {
  const item = rollOne(c, type, source, { applyPity: true });
  const useful = isUseful(c, item);
  notePity(c, type, [item], useful);
  if (c.stats) c.stats.itemsFound += 1;
  return item;
}

/** n rewards with distinct def ids; 'mixed' cycles arsenal → armor → unstable. Pity applies to the first and updates once. */
export function rollChoices(c: LootRollContext, type: OrbType | 'mixed', source: LootSource, n: number): ItemInstance[] {
  const exclude = new Set<string>();
  const items: ItemInstance[] = [];
  const count = Math.max(0, Math.floor(n));
  for (let i = 0; i < count; i++) {
    const t: OrbType = type === 'mixed' ? ORB_TYPES[i % ORB_TYPES.length] : type;
    const item = rollOne(c, t, source, { exclude, applyPity: i === 0 });
    if (exclude.has(item.defId)) continue;
    exclude.add(item.defId);
    items.push(item);
  }
  // Distinct-id fallback can come up short only if every pool is exhausted.
  if (items.length) notePity(c, type, items, items.some((i) => isUseful(c, i)));
  return items;
}

export const SHOP_BASE_PRICE: Readonly<Record<Rarity, number>> = { standard: 45, advanced: 90, prototype: 150 };

export function shopPrice(rarity: Rarity, sectorIndex: number): number {
  return Math.round(SHOP_BASE_PRICE[rarity] * (1 + 0.25 * Math.max(0, sectorIndex)));
}

/** Station stock order: always guns and armour first, an unstable item for larger shops. */
const SHOP_ORDER: readonly OrbType[] = ['arsenal', 'armor', 'armor', 'arsenal', 'unstable', 'armor', 'arsenal'];

/** Shop offers with distinct ids (no pity, no stats). */
export function rollShop(c: LootRollContext, n: number, sectorIndex: number): ShopOffer[] {
  const exclude = new Set<string>();
  const offers: ShopOffer[] = [];
  for (let i = 0; i < Math.max(0, Math.floor(n)); i++) {
    const item = rollOne(c, SHOP_ORDER[i % SHOP_ORDER.length], 'shop', { exclude });
    if (exclude.has(item.defId)) continue;
    exclude.add(item.defId);
    offers.push({ item, price: shopPrice(item.rarity, sectorIndex), sold: false });
  }
  return offers;
}

/** Distinct boss-reward upgrades usable by this mech, excluding owned ones. */
export function rollMajorUpgrades(c: LootRollContext, n: number): MajorUpgradeDef[] {
  const owned = new Set(c.inventory.upgrades);
  const pool = UPGRADE_DEF_IDS.filter((id) => {
    const def = MAJOR_UPGRADES[id];
    return !owned.has(id) && (!def.mech || def.mech === c.mech.id);
  });
  return c.rng
    .shuffle(pool)
    .slice(0, Math.max(0, Math.floor(n)))
    .map((id) => MAJOR_UPGRADES[id]);
}

/**
 * Station reroll: a different item of the same kind (same slot for equipment)
 * and rarity. Null when nothing else is available.
 */
export function rerollItem(c: LootRollContext, item: ItemInstance): ItemInstance | null {
  const owned = equippedDefIds(c.inventory);
  const kind = itemKindOf(item.defId);
  if (!kind) return null;
  const pool = idsOf(kind).filter((id) => {
    if (id === item.defId) return false;
    if (kind !== 'mod' && owned.has(id)) return false;
    if (kind === 'equipment' && EQUIPMENT[id as keyof typeof EQUIPMENT].slot !== (item as { slot?: string }).slot) return false;
    return isAvailable(c, kind, id);
  });
  const pick = c.rng.weighted(pool, (id) => baseWeight(kind, id));
  if (!pick) return null;
  const bonusModSlots = c.inventory.upgrades.includes('mod_slot') ? 1 : 0;
  return makeItem(pick, item.rarity, c.rng, { bonusModSlots });
}
